/**
 * Tree-row SELECTION state (workspace-file-aliases — expand-on-select).
 *
 * Distinct from `useWorkspaceFileStore` (which file is OPEN in the viewer
 * pane). This store tracks which tree row is currently SELECTED:
 *
 *   - single-click / tap  → SELECT the row (this store). The row expands
 *     in-place to show its full description + action buttons. The content
 *     viewer is not opened — UNLESS it is already open, in which case the
 *     caller also switches it to this file (operator 2026-08-11; the second
 *     click only ever meant "yes, actually open it", and that is already
 *     answered once the pane is up). The two stores stay independent: this one
 *     never opens anything itself, `CaseFileRow.handleClick` decides.
 *   - double-click, or the row's "Visualizar" action → additionally OPEN the
 *     file in the viewer (`useWorkspaceFileStore.openFile`).
 *
 * Only ONE row is selected at a time. Ephemeral — never persisted, and the
 * selection never leaks across cases (the caller clears it on case change,
 * mirroring the viewer pane's reset semantics).
 */

import { create } from "zustand";

/** Idle window before an unattended expanded row collapses itself (operator
 *  2026-07-27). The expanded card is for TAKING AN ACTION, not for reading —
 *  it costs ~4x a collapsed row, so it must not linger once the user has moved
 *  on. Re-click the row to bring it back. */
export const SELECTION_AUTO_COLLAPSE_MS = 10_000;

/** Ignore activity nudges closer together than this. `pointermove` fires
 *  continuously; without a floor every mouse twitch would re-render the tree. */
const ACTIVITY_THROTTLE_MS = 1_000;

interface WorkspaceTreeSelectionStore {
  /** Case-root-relative path of the selected row, or null when nothing is selected. */
  selectedPath: string | null;
  select: (path: string) => void;
  clear: () => void;

  /**
   * >0 while a row-spawned overlay is open (context menu, details dialog,
   * copy/move dialog, send-to picker). Auto-collapse is SUSPENDED while locked.
   *
   * Two reasons this is required, not defensive:
   *   1. Those overlays render in PORTALS / fixed layers, so a click inside one
   *      is "outside the tree" to a document-level listener — it would collapse
   *      the row the user is actively working in.
   *   2. Their open/closed state lives in the row component. Collapsing the row
   *      would tear the overlay down mid-interaction.
   */
  overlayLocks: number;
  lockOverlay: () => void;
  unlockOverlay: () => void;

  /** Timestamp of the last interaction with the expanded card; restarts the
   *  idle timer so the card is never yanked away from someone reading it. */
  activityAt: number;
  noteActivity: () => void;
}

export const useWorkspaceTreeSelectionStore = create<WorkspaceTreeSelectionStore>((set, get) => ({
  selectedPath: null,
  // Selecting is itself activity — otherwise a row selected 9s into a stale
  // timer would inherit the remainder and collapse almost immediately.
  select: (path) => set({ selectedPath: path, activityAt: Date.now() }),
  clear: () => set({ selectedPath: null }),

  overlayLocks: 0,
  lockOverlay: () => set((s) => ({ overlayLocks: s.overlayLocks + 1 })),
  // Floor at 0 — a double-unlock (StrictMode double-invoke, or an overlay that
  // unmounts twice) must never drive the count negative and permanently disable
  // auto-collapse.
  unlockOverlay: () => set((s) => ({ overlayLocks: Math.max(0, s.overlayLocks - 1) })),

  activityAt: 0,
  noteActivity: () => {
    const now = Date.now();
    if (now - get().activityAt < ACTIVITY_THROTTLE_MS) return;
    set({ activityAt: now });
  },
}));
